import React, { FC, useEffect, useState } from 'react'
import { View, useWindowDimensions, DeviceEventEmitter, TouchableOpacity, VirtualizedList, RefreshControl, KeyboardAvoidingView, ActivityIndicator } from 'react-native'
import Popover from 'react-native-popover-view/dist/Popover'
import { config } from '../../shared/environment'
import useBackHandler from '../../shared/hooks/useBackHandler'
import { ThemeConsumer } from '@rneui/themed'

import { bulkDeleteCategoryData, deleteCategoryData, getAllPosCategoryData } from '../../redux/slices/categorySlice'
import { CategoryListProps, FilterProps } from './categoryModule'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks'
import { useAppNavigation } from '../../types/@react-navigation/navigation'
import { filterItemsProps } from 'zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal'
import { SortOption } from '../../shared/commonComponents/commonSortModal'

import CommonHeader from '../../shared/commonComponents/commonHeader'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'
import { appColors, appFonts, fontSize } from '../../themes/appTheme'
import Toast from 'react-native-toast-message'

import { CategoryListStyle } from '../../themes/components/category/categoryListStyle'
import ZenText from 'zen-core-elements/src/elements/zenText/zenText'
import { ZenBadge, ZenButton, ZenCheckBox, ZenEmptyComponent, ZenFilterModal, ZenIcon, ZenLoader, ZenToast, } from 'zen-core-elements'
import { ListToolbar } from '../../shared/commonComponents/listToolbar'
import { shallowEqual } from 'react-redux'

const CategoryList: FC = () => {
    useBackHandler()
    //variable  used to dispatch actions
    const dispatch = useAppDispatch()
    const { value: listOfCategory, loading: loadPage, count: totalCount } = useAppSelector((state) => state?.category?.categoryDataValue)
    const { isWrite: isCategoryWrite } = useAppSelector(getScreenPermission("CategoryStack"), shallowEqual)

    const { isLandscape } = useAppSelector(state => state?.common)
    const limit = 10;
    //variable to store the navigation from useNavigation hook
    const navigation = useAppNavigation()
    //variable to store the device width
    const { width } = useWindowDimensions()
    //Variable to store the selected category items
    const [selectedCategoryCard, setSelectedCategoryCard] = useState<Set<number>>(new Set([]))
    //variable to store the count val
    const [count, setCount] = useState<number>(0)
    //variable to open/close delete popover
    const [deletePopover, setDeletePopover] = useState<boolean>(false)
    //variable to store the selected item id
    const [itemId, setItemId] = useState<number>(0)
    //variable to store the search text
    const [search, setSearch] = useState<string | null>(null)
    //variable to store the list refresh state
    const [refresh, setRefresh] = useState<boolean>(false)
    //Variable to control footer loader
    const [footerLoader, setFooterLoader] = useState(false)
    // Variable used to track offset
    const [offset, setOffset] = useState<number | null>(null)
    //variable to hold the sort list
    const sortOptionsData: SortOption[] = [
        { name: 'Category name A-Z', id: 1, field: 'category_name', order: 'ASC' },
        { name: 'Category name Z-A', id: 2, field: 'category_name', order: 'DESC' },
        { name: 'Recently created first', id: 3, field: 'created_at', order: 'DESC' },
        { name: 'Recently created last', id: 4, field: 'created_at', order: 'ASC' },
        { name: 'Recently updated first', id: 5, field: 'modified_at', order: 'DESC' },
        { name: 'Recently updated last', id: 6, field: 'modified_at', order: 'ASC' }
    ]
    //variable to store the selected sort
    const [sortOrder, setSortOrder] = useState<{ field: string, order: string, id: number } | null>(null)
    //variable to open/close filter 
    const [filterVisible, setFilterVisible] = useState<boolean>(false)
    //variable to store filter payload
    const [filterPayLoad, setFilterPayLoad] = useState<FilterProps>(null)
    // variable to store the filter data displayed
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: '1',
            filterName: 'Medium',
            filterType: "radio",
            filterValue: [
                { value: 'POS', label: 'POS' },
                { value: 'WEB', label: 'WEB' }
            ],
            initialSelected: ''
        }
    ])

    const reInitializeStates = () => {
        DeviceEventEmitter?.emit("categoryListFilter")
        setFilterPayLoad(null)
        setSearch(null)
        setSelectedCategoryCard(new Set([]))
        setSortOrder(null)
    }

    useEffect(() => {
        const categoryCardUpdate = DeviceEventEmitter.addListener("CATEGORIES_UPDATED", () => {
            reInitializeStates()
        })
        return () => {
            categoryCardUpdate?.remove()
        }
    }, [])

    //function used to handle delete confirmation popover
    const confirmation = (item: { id: number }) => {
        if (item?.id == 2) {
            selectedCategoryCard?.size > 0 ? bulkDeleteCategory() : deleteCategory()
        } else {
            setDeletePopover(false)
        }
    }

    const toggleOffset = () => {
        setOffset((prev) => prev === 0 ? -1 : 0)
    }

    useEffect(() => {
        toggleOffset()
    }, [])

    useEffect(() => {
        if (offset != null) {
            getAllPosCategory()
        }
    }, [offset])

    //useEffect which gets triggered when search changes
    useEffect(() => {
        if (search != null) {
            const searchDebounceFunction = setTimeout(() => {
                toggleOffset()
            }, 1000)
            return () => clearTimeout(searchDebounceFunction)
        }
    }, [search]);

    //useEffect to handle filter selection
    useEffect(() => {
        if (filterPayLoad != null || sortOrder != null) {
            toggleOffset()
        }
    }, [filterPayLoad, sortOrder])

    //function to fetch the category list items
    const getAllPosCategory = () => {
        const data = {
            offset: offset == -1 ? 0 : offset || 0,
            limit: limit,
            searchText: search ? search.trim() : '',
            filterData: JSON.stringify(filterPayLoad) || null,
            sortOrder: sortOrder ? JSON.stringify({ field: sortOrder.field, order: sortOrder.order }) : JSON.stringify({ field: "category_name", order: "ASC" })
        }
        let payloadData = { storeId: config?.storeId, data: data }
        dispatch(getAllPosCategoryData(payloadData)).unwrap().then((res) => {
        }).catch((err) => {
            console.log('Error in getAllCategory', err)
        }).finally(() => {
            setFooterLoader(false)
            setRefresh(false);
        })
    }

    //function used to delete the single item from the list
    const deleteCategory = async () => {
        let data = { storeId: config?.storeId, id: itemId }
        dispatch(deleteCategoryData(data)).unwrap().then((bulkDeleteRes) => {
            if (bulkDeleteRes) {
                ZenToast({ type: "success", message: "Category removed successfully" })
                setSelectedCategoryCard(new Set([]))
            }
        }).catch(() => {
            ZenToast({ type: "error", message: "Error in delete category" })
        }).finally(() => {
            setDeletePopover(false)
        })
    }

    //function used to delete multiple items from the list
    const bulkDeleteCategory = () => {
        const ids = Array.from(selectedCategoryCard)
        let data = { storeId: config?.storeId, categoryArr: ids }
        dispatch(bulkDeleteCategoryData(data)).unwrap().then((deleteRes) => {
            if (deleteRes) {
                ZenToast({ type: "success", message: "Category deleted successfully" })
                setSelectedCategoryCard(new Set([]))

            }
        }).catch(() => {
            ZenToast({ type: "error", message: "Error in bulk delete" })
        }).finally(() => {
            setDeletePopover(false)
        })
    }

    const toggleSelect = (id: number) => {
        setSelectedCategoryCard(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }
    const onLongPress = (categoryData: CategoryListProps) => {
        if (!isLandscape && selectedCategoryCard.size === 0) {
            toggleSelect(categoryData.id)
        }
    }
    const onPress = (categoryData: CategoryListProps) => {
        if (!isLandscape && selectedCategoryCard.size > 0) {
            toggleSelect(categoryData.id)
        }
    }

    const renderItem = ({ item, index }: { item: CategoryListProps[]; index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        {item?.map((categoryData: CategoryListProps, ResponseDataIndex: number) => (
                            <View key={ResponseDataIndex} style={{ flex: 1 }}>
                                <TouchableOpacity disabled={isLandscape} onLongPress={() => onLongPress(categoryData)} onPress={() => onPress(categoryData)}>
                                    {/* LIST CONTAINER */}
                                    <View style={{ padding: 10, marginHorizontal: 10, marginBottom: 10, borderRadius: 5, backgroundColor: theme.light, borderWidth: !isLandscape && selectedCategoryCard?.has(categoryData?.id) ? 1.5 : 0, borderColor: appColors.lightDark, }}                                >
                                        {/* MAIN ROW */}
                                        <View style={{ flexDirection: "row", justifyContent: "space-between" }} >
                                            {/* LEFT SECTION */}
                                            <View style={{ flexDirection: "row", flex: 1, alignItems: "center" }}  >
                                                {/* CHECKBOX (landscape / width > 600) */}
                                                {(isLandscape && isCategoryWrite) && (
                                                    <View style={{ width: 30 }}>
                                                        <ZenCheckBox
                                                            checked={selectedCategoryCard?.has(categoryData?.id)}
                                                            size={22}
                                                            checkedColor={appColors.primary}
                                                            onPress={() => toggleSelect(categoryData.id)}
                                                        />
                                                    </View>
                                                )}

                                                {/* CATEGORY NAME + BADGE */}
                                                <View style={{ flexDirection: "column", flex: 0.5, gap: 10 }}                                       >
                                                    <ZenText content={categoryData?.categoryName} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize, color: appColors.lightDark, }} />
                                                    <ZenBadge
                                                        badgeColor="#ffedbd"
                                                        badgeName={`${categoryData?.productCount}`}
                                                        badgeTextColor={theme.dark}
                                                        badgeTextSize="small"
                                                        badgeTextStyle={{ fontFamily: theme.bold, padding: 2, }}
                                                    />
                                                </View>

                                                {/* MEDIUM COLUMN */}
                                                <View style={{ flexDirection: "column", flex: 0.5, gap: 10 }}                              >
                                                    <ZenText content="Medium" numberOfLines={1} style={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: appColors.labelColor, }} />
                                                    <ZenText content={categoryData?.medium} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.smallerFontSize, color: appColors.lightDark, }} />
                                                </View>
                                            </View>

                                            {/* ACTION ICONS */}
                                            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, alignSelf: "center", }}                       >
                                                <TouchableOpacity onPress={() => navigation.navigate("AddEditCategories", { categoryId: categoryData?.id, isView: true, productCount: categoryData?.productCount })}                                      >
                                                    <ZenIcon
                                                        name="eye"
                                                        type="octicon"
                                                        color={theme.iconColor}
                                                        size={theme.iconSize}
                                                        style={CategoryListStyle?.iconStyle}
                                                    />
                                                </TouchableOpacity>
                                                {isCategoryWrite &&
                                                    <TouchableOpacity onPress={() => { navigation.navigate("AddEditCategories", { categoryId: categoryData?.id, isView: false, productCount: categoryData?.productCount }); }}                                        >
                                                        <ZenIcon
                                                            name="pencil"
                                                            type="octicon"
                                                            color={theme.iconColor}
                                                            size={theme.iconSize}
                                                            style={CategoryListStyle?.iconStyle}
                                                        />
                                                    </TouchableOpacity>
                                                }

                                                {(selectedCategoryCard?.size <= 0 && isCategoryWrite) &&
                                                    <TouchableOpacity onPress={() => { setItemId(categoryData?.id); setDeletePopover(true); }}                                >
                                                        <ZenIcon
                                                            name="delete-outline"
                                                            type="material-community"
                                                            color={appColors.red}
                                                            size={theme.iconSize}
                                                            style={CategoryListStyle?.iconStyle} />
                                                    </TouchableOpacity>
                                                }

                                            </View>
                                        </View>
                                    </View>
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>
                )}
            </ThemeConsumer>
        );
    };

    const getItem = (item: any[], index: number) => {
        let array: any = [];
        let numOfColumns = 1
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i];
            temp && array?.push(temp);
        }
        return array;
    };

    const footerComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    footerLoader ?
                        <View style={{}} >
                            <ActivityIndicator size={"small"} color={theme?.primary} />
                        </View >
                        : undefined
                )}
            </ThemeConsumer>
        )
    }

    const onEndReached = () => {
        let offsetValue = offset && offset != -1 ? offset : 0
        if (!footerLoader && (totalCount!! > offsetValue)) {
            setFooterLoader(true)
            setOffset(offsetValue + limit)
        }
    };

    const onRefresh = () => {
        setRefresh(true)
        reInitializeStates()
        toggleOffset()
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView style={{ flex: 1, backgroundColor: "#f8f8f8", paddingBottom: 10 }}>
                    <CommonHeader headerName='Category' />
                    <View style={{ flex: 1 }}>
                        <ListToolbar
                            search={{
                                value: search || "",
                                onChange: (val) => { setSearch(val as string) },
                                onCancel: () => setSearch(null),
                                placeholder: 'Search by category name',
                            }}
                            sort={{
                                selectedId: sortOrder?.id,
                                onSelect: (item) => {
                                    setSortOrder(item as { field: string, order: string, id: number });
                                },
                                title: 'Sort by',
                                options: sortOptionsData

                            }}
                            filter={{
                                count: count ?? 0,
                                onPress: () => setFilterVisible(true),
                            }}

                            buttons={
                                isCategoryWrite ? [
                                    {
                                        title: "Add Category",
                                        onPress: () => {
                                            navigation.navigate('AddEditCategories', { categoryId: 0, isView: false, productCount: 0 });
                                        }
                                    },
                                ] : []
                            }

                        />
                        {(loadPage && !refresh && !footerLoader) ?
                            <ZenLoader lottie={require('../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
                            <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 10, display: selectedCategoryCard?.size ? "flex" : "none", paddingBottom: 10 }}>
                                    <ZenCheckBox
                                        title={selectedCategoryCard?.size === listOfCategory?.length ? "Deselect All" : "Select All"}
                                        containerStyle={{ margin: 0, padding: 0, backgroundColor: "transparent", }}
                                        titleStyle={{ fontSize: theme?.mediumFontSize }}
                                        checkedColor={theme.primary}
                                        size={20}
                                        checked={selectedCategoryCard?.size === listOfCategory?.length}
                                        onPress={() => {
                                            if (selectedCategoryCard?.size === listOfCategory?.length) {
                                                // Deselect All
                                                setSelectedCategoryCard(new Set())
                                            }
                                            else {
                                                // Select All
                                                const allIds = new Set<number>()
                                                listOfCategory?.forEach((card) =>
                                                    allIds.add(card.id)
                                                )
                                                setSelectedCategoryCard(allIds)
                                            }
                                        }}
                                    />
                                    {(selectedCategoryCard.size > 0 && isLandscape) &&
                                        <ZenButton
                                            title="Delete"
                                            titleStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme.bold }}
                                            onPress={() => { selectedCategoryCard?.size > 0 ? setDeletePopover(true) : ZenToast({ type: "info", message: "Choose at least one category to remove" }) }}
                                            buttonStyle={{
                                                backgroundColor: theme?.red,
                                                borderRadius: 10,
                                                padding: 1
                                            }}
                                        />
                                    }
                                </View>
                                <VirtualizedList
                                    keyExtractor={(_: any, index: number) => index?.toString()}
                                    getItemCount={(data: any) => data?.length}
                                    getItem={getItem}
                                    scrollEnabled={true}
                                    nestedScrollEnabled={true}
                                    renderItem={renderItem}
                                    data={listOfCategory || []}
                                    keyboardDismissMode="on-drag"
                                    showsVerticalScrollIndicator={false}
                                    onEndReachedThreshold={0.01}
                                    contentContainerStyle={[{ paddingBottom: listOfCategory.length ? 10 : undefined, flexGrow: listOfCategory?.length ? undefined : 1 }]}
                                    ListFooterComponent={footerComponent}
                                    ListFooterComponentStyle={{ height: listOfCategory.length ? 70 : 0 }}
                                    ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light }} />}
                                    onEndReached={onEndReached}
                                    refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                                />
                            </View>
                        }
                    </View>
                    <ZenFilterModal
                        filterName='categoryListFilter'
                        setVisible={setFilterVisible}
                        visible={filterVisible}
                        filterItems={filterData}
                        filterCount={count}
                        setFilterCount={setCount}
                        setFilterItems={setFilterData}
                        onConfirm={(val) => {
                            let data: FilterProps = {}
                            if (val?.values?.Medium) {
                                data.medium = val?.values?.Medium
                            }
                            if (val?.values?.Availability) {
                                data.Availability = val?.values?.Availability
                            }
                            setFilterPayLoad(data)
                        }}
                        title="Category List Filter" />
                    {
                        (!isLandscape && isCategoryWrite) && (
                            <View>
                                {
                                    selectedCategoryCard?.size == 0 ?
                                        <View style={CategoryListStyle.plusIconView}>
                                            <ZenIcon
                                                name="plus"
                                                type={"material-community"}
                                                size={30}
                                                color={appColors.light}
                                                onPress={() => {
                                                    navigation.navigate('AddEditCategories', { categoryId: 0, isView: false, productCount: 0 }), setSelectedCategoryCard(new Set([]))
                                                }}
                                            />
                                        </View>
                                        :
                                        <View style={CategoryListStyle.deleteIconView2}>
                                            <ZenIcon
                                                name="delete-outline"
                                                type={"material-community"}
                                                size={30}
                                                color={appColors.light}
                                                onPress={() => { selectedCategoryCard?.size > 0 ? setDeletePopover(true) : Toast.show({ type: "info", text1: "Note", text2: "Choose at least one Category to remove" }) }}
                                            />
                                        </View>
                                }
                            </View>
                        )}
                    <Popover isVisible={deletePopover} popoverStyle={{ width: width > 900 ? 500 : 450, borderRadius: 10 }}>
                        <CommonDialogBox type='DELETE' replaceValue='category' confirmation={confirmation} />
                    </Popover>

                </KeyboardAvoidingView >
            )
            }
        </ThemeConsumer >
    );
}
export default CategoryList